import React from 'react';
import Head from 'next/head';
import NextSeo from 'next-seo';

import { Image } from 'shared-types/Common';
import { Property } from 'shared-types/Property';

import ForgottenPasswordContainer from '@routes/ForgottenPassword/containers/ForgottenPassword';
import { useTranslations } from '@lang';
import * as entityActions from '@store/entity/actions';
import { NextPage, NextPageContext } from 'next';
import { NormalizedStore } from '@store/entity/entity.types';

type ForgottenPasswordProps = {
  language: string;
  property: Property;
  propertyTitle: string;
  socialFallbackImage: Image;
  url: string;
};

const ForgottenPassword: NextPage<ForgottenPasswordProps> = ({
  language,
  property,
  propertyTitle,
  socialFallbackImage,
  url,
}) => {
  const { t } = useTranslations();
  const pageName = t('app.forgottenPassword');

  const pageTitle = propertyTitle ? `${pageName} | ${propertyTitle}` : pageName;

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <NextSeo
        config={{
          openGraph: {
            title: pageTitle,
            locale: language,
            url,
            images: [{ url: socialFallbackImage && socialFallbackImage.url }],
          },
        }}
      />
      <ForgottenPasswordContainer language={language} propertyId={property.id} />
    </>
  );
};

type ForgottenPasswordInitialProps = {
  language: string;
  property: Property;
  store: NormalizedStore;
  url: string;
} & NextPageContext;

ForgottenPassword.getInitialProps = async ({
  store,
  property,
  language,
  url,
}: ForgottenPasswordInitialProps): Promise<ForgottenPasswordProps> => {
  if (property.homepage) {
    await entityActions.fetchHomepage(property.id, language)(store.dispatch);
  }

  return {
    url,
    language,
    property,
    propertyTitle: property.propertyTitle || '',
    socialFallbackImage: property.socialFallbackImage,
  };
};

export default ForgottenPassword;
